/**
 * Reduced Phase 1 — Mystery Shopping KPI row.
 * Feature-flag gated at the caller (ReportsList). Consumes the
 * `GET /api/reports/dashboard-kpis` payload verbatim; every cell is
 * null-safe and renders an em-dash when data is insufficient.
 */
import React from 'react';

function fmtPct(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v}%`;
}

function fmtDiff(v) {
  if (v == null || Number.isNaN(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v} pp`;
}

function diffTone(v) {
  if (v == null || Number.isNaN(v)) return 'text-[#94A3B8]';
  if (v > 0) return 'text-emerald-700';
  if (v < 0) return 'text-rose-700';
  return 'text-[#475569]';
}

function Card({ label, value, sub, tone, testid }) {
  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-col justify-between min-h-[92px]"
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#64748B]">{label}</p>
      <div className="mt-2">
        <p className={`text-2xl font-['Montserrat'] font-bold leading-none ${tone || 'text-[#0F172A]'}`}>{value}</p>
        {sub && <p className="text-[11px] text-[#94A3B8] mt-1">{sub}</p>}
      </div>
    </div>
  );
}

export function KpiCardRow({ kpis }) {
  if (!kpis) return null;
  const evalScore = kpis.average_evaluation_score || {};
  const tx = kpis.trustx || {};
  const own = kpis.own_average || {};
  const comp = kpis.competitor_average || {};
  const diff = kpis.own_vs_competitor_diff_pp;

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
      data-testid="ms-kpi-row"
    >
      <Card
        label="Published Reports"
        value={kpis.published_evaluations ?? 0}
        sub={kpis.branches_represented ? `${kpis.branches_represented} branch${kpis.branches_represented === 1 ? '' : 'es'}` : null}
        testid="ms-kpi-published"
      />
      <Card
        label="Avg Evaluation Score"
        value={fmtPct(evalScore.pct)}
        sub={evalScore.n ? `n = ${evalScore.n}` : null}
        testid="ms-kpi-avg-score"
      />
      <Card
        label="TrustX®"
        value={fmtPct(tx.pct)}
        sub={tx.n ? `n = ${tx.n}` : null}
        testid="ms-kpi-trustx"
      />
      <Card
        label="Own-Store Avg"
        value={fmtPct(own.pct)}
        sub={own.n ? `n = ${own.n}` : null}
        testid="ms-kpi-own-avg"
      />
      <Card
        label="Competitor Avg"
        value={fmtPct(comp.pct)}
        sub={comp.n ? `n = ${comp.n}` : null}
        testid="ms-kpi-competitor-avg"
      />
      <Card
        label="Own vs Competitor"
        value={fmtDiff(diff)}
        sub={diff == null ? 'insufficient data' : (diff >= 0 ? 'own ahead' : 'own behind')}
        tone={diffTone(diff)}
        testid="ms-kpi-diff"
      />
    </div>
  );
}

export default KpiCardRow;
